// src/components/ui/Button.tsx — SCE Issues
// Bouton conforme au design system Sartorius (3 niveaux de hiérarchie)

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  iconLeft?: ReactNode
  iconRight?: ReactNode
  fullWidth?: boolean
  children?: ReactNode
}

/**
 * Bouton Sartorius.
 *  - primary   : fond jaune #FFED00, texte noir (CTA principal, 1-2 max par écran)
 *  - secondary : outline noir sur transparent
 *  - ghost     : texte seul, pas de fond
 */
export function Button({
  variant = 'primary',
  size = 'md',
  iconLeft,
  iconRight,
  fullWidth,
  children,
  className,
  style,
  disabled,
  ...rest
}: ButtonProps): JSX.Element {
  const styleBase: CSSProperties = {
    fontFamily: 'var(--font-family)',
    fontSize: 'var(--font-size-button)',
    lineHeight: 'var(--line-height-button)',
    fontWeight: 500,
    height: size === 'sm' ? 32 : size === 'lg' ? 48 : 40,
    paddingLeft: size === 'sm' ? 12 : 16,
    paddingRight: size === 'sm' ? 12 : 16,
    borderRadius: 'var(--radius-btn, 8px)',
    transition: 'all 0.15s ease',
    opacity: disabled ? 0.5 : 1,
    width: fullWidth ? '100%' : undefined,
  }

  const styleVariant: CSSProperties = (() => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--color-yellow)',
          color: 'var(--color-black)',
          border: 'none',
        }
      case 'secondary':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-black)',
          border: '1px solid var(--color-black)',
        }
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-black)',
          border: 'none',
        }
    }
  })()

  return (
    <button
      {...rest}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 ${className ?? ''}`}
      style={{ ...styleBase, ...styleVariant, ...style }}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  )
}
